import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, API, fmtEUR } from "@/lib/api";
import DossierTab from "@/components/DossierTab";
import ScriptTab from "@/components/ScriptTab";
import ScreenplayTab from "@/components/screenplay/ScreenplayTab";
import CastingTab from "@/components/CastingTab";
import CalendarTab from "@/components/CalendarTab";
import NotesTab from "@/components/NotesTab";
import VersionsBar from "@/components/VersionsBar";
import IcaaAdjustModal from "@/components/IcaaAdjustModal";
import BudgetTip from "@/components/BudgetTip";
import TaxCreditCalculator from "@/components/TaxCreditCalculator";
import PipelineTab from "@/components/PipelineTab";
import ProjectOverview from "@/components/ProjectOverview";
import CastingPrefillBanner from "@/components/CastingPrefillBanner";
import { SERIES_TYPES, buildTypeOptions, resolveTypeChoice } from "@/lib/project_types";
import { PROJECT_PHASES, DEFAULT_PHASE, phaseStyle } from "@/lib/project_phases";
import { DEFAULT_AREA, VALID_AREA_KEYS } from "@/lib/project_areas";
import { useSuggestions, AutocompleteInput } from "@/lib/suggestions";

const ICAA_LIMITS = { "11": 7, "12": 20 };

const CATEGORIES = ["Pública", "TV", "Internacional", "Privada", "Productora", "Fiscal"];
const STATUSES = ["previsto", "solicitado", "negociación", "confirmado", "rechazado"];

export default function ProjectEditor() {
  const { id } = useParams();
  const [structure, setStructure] = useState([]);
  const [proj, setProj] = useState(null);
  const sugg = useSuggestions();
  const [open, setOpen] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || DEFAULT_AREA;
  const tab = VALID_AREA_KEYS.has(rawTab) ? rawTab : DEFAULT_AREA;
  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === DEFAULT_AREA) next.delete("tab"); else next.set("tab", key);
    setSearchParams(next, { replace: true });
  };
  const [icaaAdjustOpen, setIcaaAdjustOpen] = useState(false);

  const saveWith = useCallback(async (p) => {
    if (!p) return;
    setSaving(true);
    try {
      await api.put(`/icaa/projects/${id}`, {
        title: p.title, project_type: p.project_type,
        phase: p.phase,
        duration_min: p.duration_min, shoot_days: p.shoot_days,
        episodes: p.episodes,
        producer: p.producer, fiscal_id: p.fiscal_id,
        director: p.director, locations: p.locations,
        producer_person: p.producer_person, screenwriters: p.screenwriters,
        items: p.items, financing: p.financing,
        tax_credit: p.tax_credit,
        dossier: p.dossier, script: p.script, calendar: p.calendar,
        project_notes: p.project_notes,
      });
      setSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  }, [id]);

  const save = useCallback(() => saveWith(proj), [proj, saveWith]);

  const applyAdjustments = (moves) => {
    const items = proj.items.map((it) => ({ ...it }));
    const byCode = Object.fromEntries(items.map((it) => [it.code, it]));
    moves.forEach((mv) => {
      const src = byCode[mv.source_code];
      if (src) {
        const curTotal = (src.quantity || 0) * (src.unit_price || 0);
        const newTotal = Math.max(0, curTotal - mv.amount_to_move);
        if ((src.quantity || 0) > 0) src.unit_price = Number((newTotal / src.quantity).toFixed(2));
        else src.unit_price = Number(newTotal.toFixed(2));
      }
      const tgt = byCode[mv.target_code];
      if (tgt) {
        const curTotal = (tgt.quantity || 0) * (tgt.unit_price || 0);
        const newTotal = curTotal + mv.amount_to_move;
        const qty = (tgt.quantity || 0) > 0 ? tgt.quantity : 1;
        tgt.quantity = qty;
        tgt.unit_price = Number((newTotal / qty).toFixed(2));
      }
    });
    const next = { ...proj, items };
    setProj(next);
    setIcaaAdjustOpen(false);
    saveWith(next);
  };
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  //
  // ---------------------------------------------------------------------------
  // INITIAL DATA LOADING
  // ---------------------------------------------------------------------------
  // The two effects below load the ICAA chapter structure (static) and the
  // project document on mount. The structure rarely changes; the project
  // belongs to the route param `id`.
  //
  useEffect(() => {
    api.get("/icaa/structure").then((s) => {
      setStructure(s.data);
      setOpen(Object.fromEntries(s.data.map((ch) => [ch.num, true])));
    });
  }, []);

  const reloadProject = useCallback(() => {
    api.get(`/icaa/projects/${id}`).then((r) => setProj(r.data));
  }, [id]);

  useEffect(() => {
    reloadProject();
  }, [id, reloadProject]);

  // Recompute chapter totals client-side from items + ICAA validation
  const totals = useMemo(() => {
    if (!proj) return { chapters: {}, grand: 0, warnings: [] };
    const chapters = (proj.items || []).reduce(
      (acc, it) => {
        const t = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
        return { ...acc, [it.chapter]: (acc[it.chapter] || 0) + t };
      },
      {}
    );
    const grand = Object.values(chapters).reduce((s, v) => s + v, 0);
    const warnings = Object.entries(ICAA_LIMITS)
      .map(([ch, maxPct]) => {
        const amount = chapters[ch] || 0;
        if (grand <= 0 || amount <= 0) return null;
        const actualPct = (amount / grand) * 100;
        if (actualPct <= maxPct + 0.05) return null;
        const maxAmount = (grand * maxPct) / 100;
        return {
          chapter: ch,
          label: ch === "11" ? "Gastos generales" : "Gastos financieros / comercial",
          limit_pct: maxPct,
          actual_pct: actualPct,
          amount,
          max_amount: maxAmount,
          exceeded_by: amount - maxAmount,
        };
      })
      .filter(Boolean);
    return { chapters, grand, warnings };
  }, [proj]);

  const finTotal = useMemo(
    () => (proj?.financing || []).reduce((s, f) => s + Number(f.amount || 0), 0),
    [proj]
  );
  const coverage = proj && totals.grand > 0 ? (finTotal / totals.grand) * 100 : 0;

  const updateItem = (code, field, val) => {
    setProj((p) => ({
      ...p,
      items: p.items.map((it) =>
        it.code === code
          ? { ...it, [field]: field === "quantity" || field === "unit_price" ? Number(val) : val }
          : it
      ),
    }));
  };

  const updateProj = (field, val) => setProj((p) => ({ ...p, [field]: val }));

  const addFinancing = () =>
    setProj((p) => ({
      ...p,
      financing: [
        ...(p.financing || []),
        { id: Date.now().toString(), name: "Nueva fuente", category: "Pública", amount: 0, status: "previsto", notes: "" },
      ],
    }));
  const updateFin = (idx, field, val) =>
    setProj((p) => ({
      ...p,
      financing: p.financing.map((f, i) =>
        i === idx ? { ...f, [field]: field === "amount" ? Number(val) : val } : f
      ),
    }));
  const removeFin = (idx) =>
    setProj((p) => ({ ...p, financing: p.financing.filter((_, i) => i !== idx) }));

  const updateTaxCredit = (patch) =>
    setProj((p) => ({ ...p, tax_credit: { ...(p.tax_credit || {}), ...patch } }));

  const addTaxCreditToFinancing = (amount, typeLabel) =>
    setProj((p) => {
      const existing = (p.financing || []).filter(
        (f) => f.id !== "tax-credit" && !(f.name || "").toLowerCase().includes("crédito fiscal")
      );
      return {
        ...p,
        financing: [
          ...existing,
          {
            id: "tax-credit",
            name: `Crédito fiscal (${typeLabel})`,
            category: "Fiscal",
            amount: Number(amount) || 0,
            status: "previsto",
            notes: "Calculado automáticamente. Editable. Art. 36 LIS.",
          },
        ],
      };
    });

  if (!proj) return (
    <div className="min-h-screen bg-gb-bg text-gb-black">
      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-8 sm:py-16">
        <p className="gb-eyebrow">Cargando proyecto</p>
        <h1 className="mt-2 gb-display text-3xl">Recuperando datos…</h1>
      </main>
    </div>
  );

  // Index items by code for quick render
  const itemsByCode = Object.fromEntries(proj.items.map((it) => [it.code, it]));

  return (
    <div className="min-h-screen bg-gb-bg font-serif text-gb-black">
      {/* Project header */}
      <section className="border-b border-gb-paper bg-gb-paper-soft/40">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-8 sm:py-8">
          <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-end md:justify-between">
            <div className="min-w-0 flex-1">
              <Link to="/" className="text-[11px] uppercase tracking-[0.35em] text-gb-muted transition hover:text-gb-red" data-testid="back-link">
                ← Proyectos
              </Link>
              <VersionsBar projectId={id} onSwitched={async () => {
                // Reload project data when active version changes
                const r = await api.get(`/icaa/projects/${id}`);
                setProj(r.data);
              }} />
              <input
                value={proj.title} onChange={(e) => updateProj("title", e.target.value)}
                data-testid="title-input"
                className="mt-2 w-full bg-transparent font-serif text-2xl tracking-tight outline-none focus:border-b focus:border-gb-red sm:text-3xl md:text-4xl"
              />
              <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-stone-600 sm:text-sm">
                <PhaseBadge phase={proj.phase || DEFAULT_PHASE} />
                <span>·</span>
                <span>{proj.project_type}</span>
                <span>·</span>
                <span>{proj.director || "Director pendiente"}</span>
                <span>·</span>
                <span>{proj.locations || "Localización pendiente"}</span>
              </p>
            </div>
            <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1 md:overflow-visible">
              <button onClick={save} disabled={saving} data-testid="save-btn"
                      className="shrink-0 rounded-full bg-gb-black px-4 py-2 text-[11px] uppercase tracking-widest text-white transition hover:bg-gb-red disabled:opacity-50 sm:px-5 sm:text-sm">
                {saving ? "Guardando…" : savedAt ? "Guardado ✓" : "Guardar"}
              </button>
              <a href={`${API}/icaa/projects/${id}/export/xlsx`}
                 download={`Presupuesto_${(proj.title || 'proyecto').replace(/ /g,'_')}.xlsx`}
                 data-testid="export-xlsx-btn"
                 className="shrink-0 rounded-full border border-stone-400 px-4 py-2 text-[11px] uppercase tracking-widest text-stone-700 transition hover:border-gb-red hover:text-gb-red sm:px-5 sm:text-sm">
                Presup. Excel
              </a>
              <a href={`${API}/icaa/projects/${id}/export/pdf`}
                 download={`Presupuesto_${(proj.title || 'proyecto').replace(/ /g,'_')}.pdf`}
                 data-testid="export-pdf-btn"
                 className="shrink-0 rounded-full border border-stone-400 px-4 py-2 text-[11px] uppercase tracking-widest text-stone-700 transition hover:border-gb-red hover:text-gb-red sm:px-5 sm:text-sm">
                Presup. PDF
              </a>
              <a href={`${API}/icaa/projects/${id}/dossier/export/pptx`}
                 download={`Dossier_${(proj.title || 'proyecto').replace(/ /g,'_')}.pptx`}
                 data-testid="export-dossier-pptx-btn"
                 className="shrink-0 rounded-full bg-gb-red px-4 py-2 text-[11px] uppercase tracking-widest text-white transition hover-gb-red sm:px-5 sm:text-sm">
                Dossier PPT
              </a>
              <a href={`${API}/icaa/projects/${id}/dossier/export/pdf`}
                 download={`Dossier_${(proj.title || 'proyecto').replace(/ /g,'_')}.pdf`}
                 data-testid="export-dossier-pdf-btn"
                 className="shrink-0 rounded-full bg-gb-red px-4 py-2 text-[11px] uppercase tracking-widest text-white transition hover-gb-red sm:px-5 sm:text-sm">
                Dossier PDF
              </a>
            </div>
          </div>

          {/* meta inputs (incluye stats compactos en summary + cards expandidos dentro) */}
          <details className="mt-5 rounded-xl border border-gb-paper bg-white/60 p-4" data-testid="project-details">
            <summary className="cursor-pointer list-none">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs uppercase tracking-widest text-gb-muted">Detalles del proyecto</span>
                <span className="hidden text-[11px] text-gb-muted sm:inline">·</span>
                <span className="flex flex-wrap items-center gap-3 text-[12px] text-gb-black/85">
                  <span className="font-semibold tabular-nums text-gb-red">{fmtEUR(totals.grand)}</span>
                  <span className="text-gb-muted/60">·</span>
                  <span>{fmtEUR(finTotal)} <span className={`ml-0.5 text-[10px] font-semibold ${coverage >= 100 ? "text-emerald-700" : "text-gb-red"}`}>
                    {coverage.toFixed(1)}%
                  </span></span>
                  <span className="text-gb-muted/60">·</span>
                  <span>{proj.shoot_days || 0} días</span>
                  {totals.warnings.length > 0 && (
                    <>
                      <span className="text-gb-muted/60">·</span>
                      <span className="rounded-full bg-gb-red/10 px-2 py-0.5 text-[10px] font-semibold text-gb-red">
                        {totals.warnings.length} aviso{totals.warnings.length > 1 ? "s" : ""} ICAA
                      </span>
                    </>
                  )}
                </span>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-gb-muted">Ver más ▾</span>
              </div>
            </summary>

            {/* Totals strip (ahora dentro del desplegable) */}
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="totals-strip">
              <StatCard label="Presupuesto total" value={fmtEUR(totals.grand)} accent />
              <StatCard label="Financiación" value={fmtEUR(finTotal)} />
              <StatCard label="Cobertura" value={`${coverage.toFixed(1)}%`}
                        hint={coverage >= 100 ? "100% cubierto" : `Falta ${fmtEUR(Math.max(0, totals.grand - finTotal))}`} />
              <StatCard label="Días de rodaje" value={proj.shoot_days || "—"} hint={proj.locations || "Sin localización"} />
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-4">
              <Meta label="Productora" value={proj.producer} onChange={(v) => updateProj("producer", v)} testid="meta-producer" suggestions={sugg.producers} />
              <Meta label="CIF/NIF" value={proj.fiscal_id} onChange={(v) => updateProj("fiscal_id", v)} testid="meta-fiscal" />
              <Meta label="Director/a" value={proj.director} onChange={(v) => updateProj("director", v)} testid="meta-director" suggestions={sugg.directors} />
              <Meta label="Productor/a" value={proj.producer_person} onChange={(v) => updateProj("producer_person", v)} testid="meta-producer-person" />
              <div className="md:col-span-2">
                <Meta label="Guionistas" value={proj.screenwriters} onChange={(v) => updateProj("screenwriters", v)} testid="meta-screenwriters" />
              </div>
              <MetaSelect
                label="Tipo"
                value={proj.project_type}
                options={buildTypeOptions(proj.project_type)}
                onChange={(v) => {
                  const next = resolveTypeChoice(v);
                  if (next) updateProj("project_type", next);
                }}
                testid="meta-type"
              />
              <MetaSelect
                label="Fase"
                value={proj.phase || DEFAULT_PHASE}
                options={PROJECT_PHASES}
                onChange={(v) => updateProj("phase", v)}
                testid="meta-phase"
              />
              {SERIES_TYPES.has(proj.project_type) && (
                <Meta label="Nº de capítulos" value={proj.episodes}
                      onChange={(v) => updateProj("episodes", Number(v) || 0)} testid="meta-episodes" />
              )}
              <Meta label="Duración (min)" value={proj.duration_min} onChange={(v) => updateProj("duration_min", Number(v) || 0)} testid="meta-duration" />
              <Meta label="Días rodaje" value={proj.shoot_days} onChange={(v) => updateProj("shoot_days", Number(v) || 0)} testid="meta-shoot-days" />
              <div className="md:col-span-2">
                <Meta label="Localizaciones" value={proj.locations} onChange={(v) => updateProj("locations", v)} testid="meta-locations" />
              </div>
            </div>
          </details>
        </div>
      </section>

      {tab === "overview" && (
        <ProjectOverview project={proj} projectId={id} onGoTab={setTab}
                         totals={totals} finTotal={finTotal} coverage={coverage} />
      )}

      {tab === "dossier" && (
        <DossierTab project={proj} projectId={id} onChange={setProj} onSave={save} />
      )}

      {tab === "script" && (
        <ScriptTab project={proj} onChange={setProj} onSave={save} onSaveProject={saveWith} />
      )}

      {tab === "screenplay" && (
        <ScreenplayTab projectId={id} project={proj} />
      )}

      {tab === "casting" && (
        <CastingTab project={proj} projectId={id} onReload={async () => {
          const r = await api.get(`/icaa/projects/${id}`);
          setProj(r.data);
        }} />
      )}

      {tab === "calendar" && (
        <CalendarTab project={proj} onChange={setProj} onSave={save} />
      )}

      {/* Budget tab */}
      {tab === "budget" && (
        <section className="mx-auto max-w-7xl px-4 py-6 sm:px-8 sm:py-8" data-testid="budget-section">
          {totals.warnings.length > 0 && (
            <div className="mb-6 rounded-xl border border-red-300 bg-red-50 p-5" data-testid="icaa-warnings-banner">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-red-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" strokeLinecap="round" />
                  </svg>
                  <h3 className="font-serif text-lg text-red-900">Atención · normativa ICAA</h3>
                </div>
                <button onClick={() => setIcaaAdjustOpen(true)} data-testid="icaa-auto-adjust-btn"
                        className="rounded-full bg-red-700 px-4 py-2 text-xs uppercase tracking-widest text-white hover:bg-red-900">
                  ✨ Auto-ajustar con Claude
                </button>
              </div>
              <ul className="space-y-1.5 text-sm text-red-800">
                {totals.warnings.map((w) => (
                  <li key={w.chapter} data-testid={`warn-cap-${w.chapter}`}>
                    <strong>Capítulo {w.chapter}</strong> ({w.label}) representa <strong>{w.actual_pct.toFixed(2)}%</strong> del
                    presupuesto. El límite ICAA es <strong>{w.limit_pct}%</strong> →
                    {" "}importe máximo imputable: <strong>{fmtEUR(w.max_amount)}</strong>.
                    Exceso: <strong>{fmtEUR(w.exceeded_by)}</strong>.
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-red-700/70">
                Referencia: Orden ECD/2784/2015 y criterios interpretativos del ICAA · Cap.11 ≤ 7% · Cap.12 ≤ 20%
              </p>
            </div>
          )}

          {/* Budget tables (scroll horizontal en móvil) */}
          {structure.map((ch) => {
            const chTotal = totals.chapters[ch.num] || 0;
            const pct = totals.grand > 0 ? (chTotal / totals.grand) * 100 : 0;
            const limit = ICAA_LIMITS[ch.num];
            const exceeds = limit !== undefined && pct > limit + 0.05;
            const isOpen = open[ch.num];
            return (
              <div key={ch.num} className="mb-3 overflow-hidden rounded-xl border border-stone-300/70 bg-white">
                <div
                  onClick={() => setOpen((o) => ({ ...o, [ch.num]: !o[ch.num] }))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpen((o) => ({ ...o, [ch.num]: !o[ch.num] }));
                    }
                  }}
                  data-testid={`chapter-toggle-${ch.num}`}
                  className={`flex w-full cursor-pointer items-center justify-between px-6 py-4 text-left text-[#FAFAF6] ${
                    exceeds ? "bg-red-700" : "bg-stone-900"
                  }`}
                >
                  <span className="flex items-center gap-4">
                    <span className="font-mono text-xs text-[#26775E]">{ch.num}</span>
                    <span className="font-serif text-base">{ch.title}</span>
                    {limit !== undefined && (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                        exceeds ? "bg-white text-red-700" : "bg-white/15 text-white/70"
                      }`} data-testid={`cap-pct-${ch.num}`}>
                        {pct.toFixed(1)}% / max {limit}%
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-4">
                    <span className="font-mono text-sm tabular-nums">{fmtEUR(chTotal)}</span>
                    <span onClick={(e) => e.stopPropagation()}><BudgetTip chapter={ch.num} /></span>
                    <span className={`transition-transform ${isOpen ? "rotate-180" : ""}`}>▾</span>
                  </span>
                </div>
                {isOpen && ch.num === "02" && (proj.casting?.characters?.length || 0) > 0 && (
                  <CastingPrefillBanner project={proj} projectId={id} onApplied={reloadProject} />
                )}
                {isOpen && ch.subchapters.map((sub) => (
                  <div key={sub.code}>
                    <div className="bg-[#F1EEE5]/40 px-4 py-2 text-xs uppercase tracking-widest text-stone-700 sm:px-6">
                      <span className="mr-3 font-mono">{sub.code}</span>
                      {sub.title}
                    </div>
                    <div className="overflow-x-auto">
                    <table className="w-full min-w-[680px] text-sm">
                      <tbody>
                        {sub.accounts.map(([code, defaultDesc]) => {
                          const it = itemsByCode[code] || { code, description: defaultDesc, unit: "u", quantity: 0, unit_price: 0 };
                          const total = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                          return (
                            <tr key={code} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`row-${code}`}>
                              <td className="w-24 px-6 py-2 font-mono text-xs text-stone-500">{code}</td>
                              <td className="px-2 py-2">
                                <input
                                  value={it.description}
                                  onChange={(e) => updateItem(code, "description", e.target.value)}
                                  data-testid={`desc-${code}`}
                                  className="w-full bg-transparent text-stone-900 outline-none focus:border-b focus:border-[#26775E]"
                                />
                              </td>
                              <td className="w-20 px-2 py-2">
                                <input
                                  value={it.unit}
                                  onChange={(e) => updateItem(code, "unit", e.target.value)}
                                  data-testid={`unit-${code}`}
                                  className="w-full bg-transparent text-center text-stone-700 outline-none"
                                />
                              </td>
                              <td className="w-24 px-2 py-2">
                                <input type="number" step="0.01" value={it.quantity}
                                       onChange={(e) => updateItem(code, "quantity", e.target.value)}
                                       data-testid={`qty-${code}`}
                                       className="w-full bg-transparent text-right tabular-nums outline-none" />
                              </td>
                              <td className="w-32 px-2 py-2">
                                <input type="number" step="0.01" value={it.unit_price}
                                       onChange={(e) => updateItem(code, "unit_price", e.target.value)}
                                       data-testid={`price-${code}`}
                                       className="w-full bg-transparent text-right tabular-nums outline-none" />
                              </td>
                              <td className="w-36 px-6 py-2 text-right font-mono tabular-nums">
                                <span className={total > 0 ? "text-stone-900" : "text-stone-300"}>
                                  {fmtEUR(total)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}

          <div className="mt-6 flex items-center justify-between rounded-xl bg-[#26775E] p-6 text-white" data-testid="grand-total">
            <span className="text-sm uppercase tracking-[0.3em]">Total presupuesto</span>
            <span className="font-mono text-3xl tabular-nums">{fmtEUR(totals.grand)}</span>
          </div>
        </section>
      )}

      {/* Financing tab */}
      {tab === "financing" && (
        <section className="mx-auto max-w-7xl px-4 py-6 sm:px-8 sm:py-8" data-testid="financing-section">
          <div className="mb-4 rounded-xl border border-[#26775E]/30 bg-[#26775E]/5 px-4 py-3 text-sm" data-testid="funding-kb-banner">
            <Link to="/funding" className="flex flex-wrap items-center justify-between gap-2 text-stone-800 hover:text-[#26775E]">
              <span>
                <strong className="text-[#26775E]">📚 Base de conocimiento:</strong> ICAA, incentivos fiscales por
                territorio y convocatorias autonómicas.
              </span>
              <span className="rounded-full bg-[#26775E] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white">
                Ver ayudas →
              </span>
            </Link>
          </div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-serif text-xl sm:text-2xl">Plan de financiación</h3>
            <button onClick={addFinancing} data-testid="add-financing-btn"
                    className="rounded-full bg-[#26775E] px-4 py-2 text-[11px] uppercase tracking-widest text-white hover:bg-stone-900 sm:px-5 sm:text-sm">
              + Añadir fuente
            </button>
          </div>

          <div className="overflow-x-auto rounded-xl border border-stone-300/70 bg-white">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="bg-stone-900 text-left text-xs uppercase tracking-widest text-[#FAFAF6]">
                  <th className="px-4 py-3">Fuente</th>
                  <th className="px-4 py-3">Categoría</th>
                  <th className="px-4 py-3 text-right">Importe</th>
                  <th className="px-4 py-3 text-right">%</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Notas</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {(proj.financing || []).map((f, i) => {
                  const pct = finTotal > 0 ? (Number(f.amount || 0) / finTotal) * 100 : 0;
                  return (
                    <tr key={f.id || i} className="border-t border-stone-100" data-testid={`fin-row-${i}`}>
                      <td className="px-4 py-2">
                        <input value={f.name} onChange={(e) => updateFin(i, "name", e.target.value)}
                               data-testid={`fin-name-${i}`}
                               className="w-full bg-transparent outline-none focus:border-b focus:border-[#26775E]" />
                      </td>
                      <td className="px-4 py-2">
                        <select value={f.category} onChange={(e) => updateFin(i, "category", e.target.value)}
                                data-testid={`fin-cat-${i}`}
                                className="bg-transparent outline-none">
                          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <input type="number" step="0.01" value={f.amount}
                               onChange={(e) => updateFin(i, "amount", e.target.value)}
                               data-testid={`fin-amount-${i}`}
                               className="w-32 bg-transparent text-right tabular-nums outline-none" />
                      </td>
                      <td className="px-4 py-2 text-right text-stone-500">{pct.toFixed(1)}%</td>
                      <td className="px-4 py-2">
                        <select value={f.status} onChange={(e) => updateFin(i, "status", e.target.value)}
                                data-testid={`fin-status-${i}`}
                                className="bg-transparent outline-none">
                          {STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <input value={f.notes || ""} onChange={(e) => updateFin(i, "notes", e.target.value)}
                               data-testid={`fin-notes-${i}`}
                               className="w-full bg-transparent outline-none" />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => removeFin(i)} data-testid={`fin-remove-${i}`}
                                className="rounded-full px-2 text-stone-400 hover:text-red-600">×</button>
                      </td>
                    </tr>
                  );
                })}
                {(!proj.financing || proj.financing.length === 0) && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-500">
                    Aún no hay fuentes de financiación. Pulsa &quot;+ Añadir fuente&quot;.
                  </td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-[#26775E] text-white">
                  <td className="px-4 py-3 font-medium uppercase tracking-widest">Total</td>
                  <td />
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtEUR(finTotal)}</td>
                  <td className="px-4 py-3 text-right">
                    {totals.grand > 0 ? `${coverage.toFixed(1)}%` : "—"}
                  </td>
                  <td colSpan={3} className="px-4 py-3 text-sm">
                    {coverage >= 100
                      ? "Presupuesto totalmente cubierto"
                      : `Por cubrir: ${fmtEUR(Math.max(0, totals.grand - finTotal))}`}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <TaxCreditCalculator
            project={proj}
            onUpdate={updateTaxCredit}
            onAddToFinancing={addTaxCreditToFinancing}
          />
        </section>
      )}

      {tab === "pipeline" && (
        <PipelineTab project={proj} />
      )}

      {tab === "notes" && (
        <NotesTab project={proj}
                  onChange={(p) => setProj(p)}
                  onSave={save} />
      )}

      {icaaAdjustOpen && (
        <IcaaAdjustModal
          projectId={id}
          currentItems={proj.items}
          currentTotal={totals.grand}
          onClose={() => setIcaaAdjustOpen(false)}
          onApply={applyAdjustments}
        />
      )}
    </div>
  );
}

const StatCard = ({ label, value, hint, accent }) => (
  <div className={`rounded-xl border p-4 transition ${accent ? "border-gb-red bg-white shadow-sm" : "border-gb-paper bg-white/70"}`}>
    <p className="gb-label">{label}</p>
    <p className={`mt-1 font-serif text-2xl ${accent ? "text-gb-red" : "text-gb-black"}`}>{value}</p>
    {hint && <p className="mt-1 text-xs text-gb-muted">{hint}</p>}
  </div>
);

const TabButton = ({ label, active, onClick, testid, badge }) => (
  <button onClick={onClick} data-testid={testid}
          className={`relative rounded-full px-5 py-2 text-sm uppercase tracking-widest transition ${
            active ? "bg-gb-black text-white" : "bg-white/70 text-gb-black hover:bg-gb-paper"
          }`}>
    {label}
    {badge ? (
      <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-gb-red px-1.5 text-[10px] font-medium text-white">
        {badge}
      </span>
    ) : null}
  </button>
);

const Meta = ({ label, value, onChange, testid, suggestions }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span>
    {suggestions ? (
      <AutocompleteInput value={value ?? ""} onChange={onChange} options={suggestions}
        data-testid={testid}
        className="mt-1 w-full rounded border border-stone-300 bg-white px-3 py-1.5 font-serif text-sm outline-none focus:border-[#26775E]" />
    ) : (
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="mt-1 w-full rounded border border-stone-300 bg-white px-3 py-1.5 font-serif text-sm outline-none focus:border-[#26775E]" />
    )}
  </label>
);

const MetaSelect = ({ label, value, options, onChange, testid }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span>
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 w-full rounded border border-stone-300 bg-white px-3 py-1.5 font-serif text-sm outline-none focus:border-[#26775E]"
    >
      {options.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
    </select>
  </label>
);

const PhaseBadge = ({ phase }) => {
  const sty = phaseStyle(phase);
  return (
    <span
      data-testid="phase-badge"
      className={`inline-flex items-center gap-1.5 rounded-full ${sty.bg} ${sty.text} px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${sty.dot}`} />
      {sty.label}
    </span>
  );
};
